/**
 * First-touch signup attribution (task sheet Oct T2).
 *
 * Captured on the first page view that carries UTM parameters or an ad click id,
 * stored in localStorage on the shared gcbuying.com origin, and sent with the
 * register request. First touch wins; the value expires after 30 days. The
 * landing site (frontend-next) and the web app (frontend) keep an identical copy
 * of this file — they share no package.
 */
export const ATTRIBUTION_KEY = 'gc_attribution'
export const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000

export interface Attribution {
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmContent?: string
  adChannel?: string
}

interface Stored extends Attribution {
  referrer?: string
  capturedAt: string
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>

const CLICK_ID_CHANNEL: Record<string, string> = { gclid: 'google', fbclid: 'meta', ttclid: 'tiktok' }
// Charset: letters, digits, space and the punctuation ad-ops naming uses
// (_ . - + ( ) / : | , & = ! # %). Markup, quotes and control chars are dropped,
// not truncated. Keep in sync with apps/auth/src/users/attribution.ts.
const SAFE = /^[A-Za-z0-9 _.\-+()/:|,&=!#%]{1,120}$/
const clip = (v: string | null): string | undefined => {
  const t = v?.trim()
  return t && SAFE.test(t) ? t : undefined
}

export function parseAttribution(search: string, _referrer: string): Attribution | undefined {
  const p = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const out: Attribution = {}
  const source = clip(p.get('utm_source'))
  const medium = clip(p.get('utm_medium'))
  const campaign = clip(p.get('utm_campaign'))
  const content = clip(p.get('utm_content'))
  if (source) out.utmSource = source
  if (medium) out.utmMedium = medium
  if (campaign) out.utmCampaign = campaign
  if (content) out.utmContent = content
  for (const [id, channel] of Object.entries(CLICK_ID_CHANNEL)) {
    if (p.get(id)) {
      out.adChannel = channel
      break
    }
  }
  return Object.keys(out).length ? out : undefined
}

export function captureAttribution(search: string, referrer: string, storage: StorageLike, now: Date): void {
  try {
    if (readStoredAttribution(storage, now)) return // first touch wins
    const parsed = parseAttribution(search, referrer)
    if (!parsed) return
    let host: string | undefined
    try {
      host = referrer ? new URL(referrer).hostname : undefined
    } catch {
      host = undefined
    }
    const stored: Stored = { ...parsed, capturedAt: now.toISOString() }
    if (host) stored.referrer = host
    storage.setItem(ATTRIBUTION_KEY, JSON.stringify(stored))
  } catch {
    /* storage unavailable (private mode) — attribution is best effort */
  }
}

export function readStoredAttribution(storage: StorageLike, now: Date): Attribution | undefined {
  try {
    const raw = storage.getItem(ATTRIBUTION_KEY)
    if (!raw) return undefined
    const stored = JSON.parse(raw) as Stored
    const at = Date.parse(stored.capturedAt)
    if (!Number.isFinite(at) || now.getTime() - at > ATTRIBUTION_TTL_MS) return undefined
    const { utmSource, utmMedium, utmCampaign, utmContent, adChannel } = stored
    const out: Attribution = {}
    if (utmSource) out.utmSource = utmSource
    if (utmMedium) out.utmMedium = utmMedium
    if (utmCampaign) out.utmCampaign = utmCampaign
    if (utmContent) out.utmContent = utmContent
    if (adChannel) out.adChannel = adChannel
    return Object.keys(out).length ? out : undefined
  } catch {
    return undefined
  }
}
